import React, { useCallback, useEffect, useMemo, useState } from "react";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapContainer, Marker, Popup, TileLayer, Tooltip } from "react-leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { BuildingAPI, DashboardAPI, resolveAsset } from "../../services/api";
import { ReactComponent as BuildingIcon } from "../../assets/icons/dashboard/building.svg";
import { ReactComponent as MeetingRoomIcon } from "../../assets/icons/dashboard/meetingroom.svg";
import { ReactComponent as QrcodeScannerIcon } from "../../assets/icons/dashboard/qrcodescanner.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/dashboard/eye.svg";
import { ReactComponent as RefreshIcon } from "../../assets/icons/dashboard/refresh.svg";
import { ReactComponent as LocationIcon } from "../../assets/icons/dashboard/location.svg";
import { ReactComponent as MagnifyingGlassIcon } from "../../assets/icons/dashboard/magnifyingglass.svg";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const statCards = [
  {
    titleKey: "admin.dashboard.registeredBuildings",
    value: "3",
    tone: "blue",
    point: "ตึก",
    icon: BuildingIcon,
  },
  {
    titleKey: "admin.dashboard.registeredLocations",
    value: "35",
    tone: "purple",
    point: "ห้อง",
    icon: MeetingRoomIcon,
  },
  {
    titleKey: "admin.dashboard.totalQrCodes",
    value: "50",
    tone: "green",
    point: "จุด",
    icon: QrcodeScannerIcon,
  },
  {
    titleKey: "admin.dashboard.totalUsage",
    value: "12.4k",
    tone: "orange",
    point: "ครั้ง",
    icon: EyeIcon,
  },
];

const topRoutes = [
  {
    name: "QR ทางเข้าใหญ่ A → ห้องตรวจ..",
    sub: "อาคารเฉลิมพระบารมี โรงพยาบาล..",
    scans: 2145,
  },
  { name: "หน้าลิฟต์โซน B → ห้องตรวจ..", sub: "อาคารสุริโยโน", scans: 1327 },
  { name: "หน้าลิฟต์โซน A → ห้องตรวจ..", sub: "อาคารสุริโยโน", scans: 958 },
];

const buildingsMock = [
  {
    id: "HPT-001",
    name: "อาคารเฉลิมพระบารมี โรงพยาบาล..",
    address: "เลขที่ 110 ถนนกัลยาณี ตำบลสุเทพ อ.เมือง เชียงใหม่ 50200",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRm2_M9IwFl9lbmELWVlLzX_T-P1_3MKYFTKw&s",
    status: "เผยแพร่",
  },
  {
    id: "HPT-002",
    name: "อาคารสุริโยโน",
    address: "Suthep, 110 ถนนนิมมานฯ, เชียงใหม่ 50200",
    img: "https://w2.med.cmu.ac.th/suandok-variety/wp-content/uploads/2021/01/141008846_904092800406350_2625982473013800007_n-1-scaled-1200x630.jpg",
    status: "เผยแพร่",
  },
  {
    id: "HPT-003",
    name: "อาคารบุญสม มาร์ติน",
    address: "ตำบลสุเทพ อำเภอเมืองเชียงใหม่ เชียงใหม่ 50200",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS2Q6GTuRS52QH2P9_h0o-SaPcGbayiF1sWMQ&s",
    status: "เผยแพร่",
  },
];

function statCardBg(tone) {
  // Pastel card background color
  switch (tone) {
    case "blue":
      return "bg-white border-slate-200";
    case "purple":
      return "bg-white border-slate-200";
    case "green":
      return "bg-white border-slate-200";
    case "orange":
      return "bg-white border-slate-200";
    default:
      return "bg-white border-slate-200";
  }
}

const TextPill = (tone) => {
  switch (tone) {
    case "orange":
      return "text-orange-900";
    case "green":
      return "text-green-900";
    case "blue":
      return "text-blue-900";
    case "purple":
      return "text-violet-900";
    default:
      return "text-slate-900";
  }
};

const Text = (tone) => {
  switch (tone) {
    case "orange":
      return "text-orange-600";
    case "green":
      return "text-green-600";
    case "blue":
      return "text-blue-600";
    case "purple":
      return "text-violet-600";
    default:
      return "text-slate-600";
  }
};

const IconPill = (tone) => {
  switch (tone) {
    case "orange":
      return "bg-orange-50 text-orange-600";
    case "green":
      return "bg-green-50 text-green-600";
    case "blue":
      return "bg-blue-50 text-blue-600";
    case "purple":
      return "bg-violet-50 text-violet-600";
    default:
      return "bg-slate-50 text-slate-600";
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [buildingPage, setBuildingPage] = useState(1);
  const [buildingTotalPages, setBuildingTotalPages] = useState(1);
  const [buildingsData, setBuildingsData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDashboardData = useCallback(() => {
    setLoading(true);
    DashboardAPI.overview()
      .then(setOverview)
      .catch(() => setOverview(null))
      .finally(() => setLoading(false));
  }, []);

  const handleSearchChange = (val) => {
    setQ(val);
    setBuildingPage(1);
  };

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const params = { page: buildingPage, limit: 3, pageSize: 3 };
    if (q.trim()) {
      params.q = q.trim();
    }

    BuildingAPI.list(params)
      .then((r) => {
        const items = r?.items || r?.buildings || (Array.isArray(r) ? r : []);
        setBuildingsData(items);
        setBuildingTotalPages(
          r?.totalPages ||
          Math.ceil((r?.total || items.length || 0) / (r?.pageSize || r?.limit || 3)) ||
          1
        );
      })
      .catch(() => {
        setBuildingsData(null);
        setBuildingTotalPages(1);
      });
  }, [buildingPage, q]);

  const stats = overview?.stats;
  const liveMap = overview?.liveMap;
  const geoPins = useMemo(
    () =>
      (liveMap?.pins || [])
        .map((pin) => ({
          id: pin.id,
          name: pin.name || pin.buildingName || pin.code || "อาคาร",
          address: pin.address,
          status: pin.status,
          lat: Number(pin.lat ?? pin.latitude),
          lng: Number(pin.lng ?? pin.longitude),
        }))
        .filter((pin) => Number.isFinite(pin.lat) && Number.isFinite(pin.lng)),
    [liveMap]
  );
  const geoCenter = geoPins[0] || { lat: 18.7893, lng: 98.9746 };

  const cards = stats
    ? [
      { ...statCards[0], value: String(stats.registeredBuildingsCount ?? 0) },
      { ...statCards[1], value: String(stats.registeredLocationsCount ?? 0) },
      { ...statCards[2], value: String(stats.totalQrCodes ?? 0) },
      { ...statCards[3], value: String(stats.totalUsage ?? 0) },
    ]
    : statCards;

  const routes = overview?.topRoutes?.length ? overview.topRoutes : topRoutes;

  const buildings = useMemo(() => {
    if (buildingsData) return buildingsData;
    const overviewBuildings = overview?.buildings?.items;
    if (overviewBuildings) return overviewBuildings;
    const s = q.trim().toLowerCase();
    if (!s) return buildingsMock;
    return buildingsMock.filter(
      (b) =>
        (b.name || "").toLowerCase().includes(s) ||
        String(b.code || b.id || "").toLowerCase().includes(s),
    );
  }, [q, buildingsData, overview]);

  const pinBounds = useMemo(() => {
    const pins = liveMap?.pins || [];
    return pins.reduce(
      (bounds, pin) => ({
        maxX: Math.max(bounds.maxX, Number(pin.x) || 0),
        maxY: Math.max(bounds.maxY, Number(pin.y) || 0),
      }),
      {
        maxX: Number(liveMap?.imageWidth) || 0,
        maxY: Number(liveMap?.imageHeight) || 0,
      }
    );
  }, [liveMap]);

  const getPinStyle = (pin) => {
    const x = Number(pin.x) || 0;
    const y = Number(pin.y) || 0;
    const hasImageSize = Number(liveMap?.imageWidth) && Number(liveMap?.imageHeight);

    if (hasImageSize) {
      return {
        left: `${(x / Number(liveMap.imageWidth)) * 100}%`,
        top: `${(y / Number(liveMap.imageHeight)) * 100}%`,
      };
    }

    return {
      left: `${pinBounds.maxX ? (x / pinBounds.maxX) * 100 : 50}%`,
      top: `${pinBounds.maxY ? (y / pinBounds.maxY) * 100 : 50}%`,
    };
  };

  return (
    <div className="flex flex-col gap-4">
      {/* STATS CARDS */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {cards.map((c) => {
          const Icon = c.icon;

          return (
            <div
              key={c.title}
              className={[
                "rounded-xl border p-5 shadow-sm",
                statCardBg(c.tone),
              ].join(" ")}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`shrink-0 w-16 h-16 rounded-full grid place-items-center ${IconPill(c.tone)}`}
                >
                  <Icon className="w-6 h-6" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold ${Text(c.tone)}`}>
                    {t(c.titleKey)}
                  </div>

                  <div className="flex items-end gap-6 mt-2">
                    <div className={`text-3xl ml-4 font-bold ${TextPill(c.tone)}`}>
                      {c.value}
                    </div>

                    <div className="text-sm font-semibold text-slate-600 mb-1">
                      {c.point}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* MAP + TOP ROUTES */}
      <section className="grid grid-cols-1 xl:grid-cols-[1.6fr_0.9fr] gap-4">
        {/* MAP PLACEHOLDER */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 mr-1" />
              <div className="font-bold text-sm text-slate-900">Live Map Tracking</div>
            </div>
            <button
              onClick={loadDashboardData}
              disabled={loading}
              className="text-blue-500 flex text-sm disabled:opacity-60"
            >
              <RefreshIcon className="w-2.5 h-2.5 mt-1 mr-2 text-blue-500 " />{" "}
              {loading ? t("admin.dashboard.loading") : t("admin.dashboard.refresh")}
            </button>
          </div>

          {geoPins.length ? (
            <div className="h-[300px]">
              <MapContainer
                center={[geoCenter.lat, geoCenter.lng]}
                zoom={16}
                scrollWheelZoom
                className="h-full w-full"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {geoPins.map((pin, idx) => (
                  <Marker key={pin.id || idx} position={[pin.lat, pin.lng]}>
                    <Tooltip direction="top" offset={[0, -32]} opacity={0.95}>
                      <span className="font-bold">{pin.name}</span>
                    </Tooltip>
                    <Popup>
                      <div className="min-w-[180px]">
                        <div className="font-bold text-slate-900">{pin.name}</div>
                        {pin.address && (
                          <div className="mt-1 text-xs text-slate-500">{pin.address}</div>
                        )}
                        <div className="mt-2 text-xs text-slate-400">
                          Lat: {pin.lat.toFixed(6)}, Lng: {pin.lng.toFixed(6)}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          ) : (
            <div
              className="relative h-[300px] bg-cover bg-center"
              style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.55)), url(${resolveAsset(liveMap?.mapImageUrl) || "https://images.unsplash.com/photo-1526779259212-939e64788e3c?auto=format&fit=crop&w=1600&q=60"})`,
              }}
            >
              {liveMap?.pins?.length ? (
                liveMap.pins.map((pin, idx) => (
                  <LocationIcon
                    key={idx}
                    className="absolute w-5 h-5 text-blue-500 -translate-x-1/2 -translate-y-full"
                    style={getPinStyle(pin)}
                  />
                ))
              ) : (
                <>
                  <LocationIcon className="absolute left-[62%] top-[38%] w-5 h-5 text-blue-500 " />
                  <LocationIcon className="absolute left-[58%] top-[52%] w-5 h-5 text-blue-500 " />
                  <LocationIcon className="absolute left-[68%] top-[62%] w-5 h-5 text-blue-500 " />
                </>
              )}
            </div>
          )}
        </div>

        {/* TOP ROUTES PANEL */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="font-bold text-slate-900">
              {t("admin.dashboard.topRoutes")}
            </div>
          </div>

          <div>
            {/* HEADER ROW */}
            <div className="grid grid-cols-[1fr_auto] gap-4 bg-slate-50 px-5 py-4 text-sm font-bold text-slate-500">
              <div>{t("admin.dashboard.route")}</div>
              <div className="text-right">{t("admin.dashboard.scans")}</div>
            </div>

            {/* DATA ROWS */}
            <div>
              {routes.map((r, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_auto] gap-4 bg-white px-5 py-4"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-base text-blue-800 truncate">
                      {r.scanLocation || r.from || r.startName || "ตำแหน่งที่สแกน"}
                      <span className="mx-2 text-xl text-blue-800">→</span>
                      {r.destinationName || r.to || r.roomName || r.name || "ห้องที่เลือก"}
                    </div>

                    <div className="text-sm text-slate-600 truncate">
                      {r.building || "อาคาร"}
                    </div>
                  </div>

                  <div className="font-bold text-2xl mr-4 text-blue-800 shrink-0 text-right">
                    {(r.scans ?? 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>


            <button
              onClick={() => navigate("/admin/top-routes")}
              className="mt-4 w-full border border-slate-200 py-4 font-semibold text-base text-blue-500 hover:bg-slate-50"
            >
              {t("admin.dashboard.viewAll")}
            </button>
          </div>
        </div>
      </section>

      {/* BUILDINGS LIST */}
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="font-semibold text-lg text-slate-900">
            {t("admin.dashboard.allBuildings")}
          </div>

          <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 min-w-[220px] sm:min-w-[320px] bg-white">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-500" />

            <input
              className="w-full outline-none font-base text-base placeholder:text-slate-500"
              value={q}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t("admin.dashboard.searchPlaceholder")}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          {buildings.map((b) => (
            <div
              key={b.id}
              className="w-[300px] h-[400px] border border-slate-200 rounded-2xl overflow-hidden bg-white flex flex-col"
            >
              {/* Building thumbnail */}
              <div className="h-[200px] rounded-xl overflow-hidden m-2 relative">
                <div
                  className="w-full h-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${resolveAsset(b.imageUrl || b.img) || buildingsMock[0].img})` }}
                >
                  <div className="absolute top-2.5 right-2.5 bg-emerald-700/80 border border-green-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                    {b.status === "published" ? "เผยแพร่" : b.status}
                  </div>
                </div>
              </div>

              {/* Building info */}
              <div className="m-3 flex flex-col flex-1">
                <div className="m-1 -mt-1 text-sm text-slate-400"># &nbsp;ID: {b.code || b.id}</div>

                <div className="mt-2 m-1 font-semibold text-slate-900">{b.name}</div>

                <div className="-mt-1 m-1 text-xs text-slate-500 leading-relaxed min-h-[34px]">
                  {b.address}
                </div>

                <button
                  onClick={() => navigate(`/admin2?buildingId=${encodeURIComponent(b.id)}`)}
                  className="mt-auto w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg py-3"
                >
                  {t("admin.dashboard.buildingSettings")}
                </button>
              </div>
            </div>
          ))}
        </div>

        {!buildings.length && (
          <div className="py-10 text-center text-sm font-bold text-slate-400">
            {t("admin.dashboard.noBuildingFound")}
          </div>
        )}

        {/* PAGINATION */}
        {buildingTotalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-5">
            <button
              disabled={buildingPage <= 1}
              onClick={() => setBuildingPage(buildingPage - 1)}
              className=" bg-white rounded-xl px-3 py-1.5 font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              ‹
            </button>

            {Array.from({ length: buildingTotalPages }, (_, i) => i + 1).map((p) => {
              const isCurrent = p === buildingPage;
              return (
                <button
                  key={p}
                  onClick={() => setBuildingPage(p)}
                  className={
                    "rounded-xl px-2 py-1.5 text-sm font-semibold border " +
                    (isCurrent
                      ? "border-blue-600 bg-blue-600 text-white font-semibold"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                  }
                >
                  {String(p).padStart(2, "0")}
                </button>
              );
            })}

            <button
              disabled={buildingPage >= buildingTotalPages}
              onClick={() => setBuildingPage(buildingPage + 1)}
              className=" bg-white rounded-xl px-3 py-1.5 font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              ›
            </button>
          </div>
        )}
      </section>
      {/* Footer */}
      <div className="text-left border border-slate-200 rounded-lg p-4 bg-white text-xs text-slate-500">
        {t("admin.feedbackDetail.copyright")}
      </div>
    </div>
  );
}
